import {
  KeyboardArrowDown,
  KeyboardArrowRight,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import get from "lodash/get";
import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import * as XLSX from "xlsx";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import group from "../../services/group";
import report from "../../services/report";
import queryDefault from "../../utils/queryDefault";

type MonthlyIncome = {
  month: number;
  month_name: string;
  total_income: number;
  total_kertas: number;
};

type BranchItem = {
  branch_id: number;
  branch_name: string;
  monthly_income: MonthlyIncome[];
  total_annual_income: number;
  total_annual_kertas: number;
};

type BranchGroupItem = {
  branch_group_id: number;
  branch_group_name: string;
  monthly_income: MonthlyIncome[];
  total_annual_income: number;
  total_annual_kertas: number;
  branches: BranchItem[];
};

type DashboardFiltersForm = {
  year: number | string;
  branch_groups: any[];
  region: string | null;
};

type DashboardFilters = {
  year: number;
  branchGroupIds: number[];
  region: string;
};

type DashboardTab = "summary" | "sales";
type SalesTab = "branch_group_details" | "branch_group_ranking" | "branch_ranking";

const MONTHS = [
  { month: 1, month_name: "January" },
  { month: 2, month_name: "February" },
  { month: 3, month_name: "March" },
  { month: 4, month_name: "April" },
  { month: 5, month_name: "May" },
  { month: 6, month_name: "June" },
  { month: 7, month_name: "July" },
  { month: 8, month_name: "August" },
  { month: 9, month_name: "September" },
  { month: 10, month_name: "October" },
  { month: 11, month_name: "November" },
  { month: 12, month_name: "December" },
];

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || fallback;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("id-ID", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatNumber = (value: number) => formatCurrency(value);

const findMonthIncome = (monthlyIncome: MonthlyIncome[] = [], month: number) => {
  const item = monthlyIncome.find((entry) => Number(entry.month) === Number(month));
  return Number(item?.total_income || 0);
};

const findMonthKertas = (monthlyIncome: MonthlyIncome[] = [], month: number) => {
  const item = monthlyIncome.find((entry) => Number(entry.month) === Number(month));
  return Number(item?.total_kertas || 0);
};

const normalizeParams = (filters: DashboardFilters) => {
  const params: any = {
    year: Number(filters.year),
  };

  if (filters.branchGroupIds.length > 0) {
    params.branch_group_ids = filters.branchGroupIds.join(",");
  }

  if (String(filters.region || "").trim()) {
    params.region = String(filters.region).trim();
  }

  return params;
};

export default function ReportDashboard() {
  const notification = useNotification();
  const currentYear = new Date().getFullYear();
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [downloadError, setDownloadError] = useState("");
  const [salesDownloadError, setSalesDownloadError] = useState("");
  const [isSalesDownloading, setIsSalesDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState<DashboardTab>("summary");
  const [activeSalesTab, setActiveSalesTab] = useState<SalesTab>("branch_group_details");
  const [filters, setFilters] = useState<DashboardFilters>({
    year: currentYear,
    branchGroupIds: [],
    region: "",
  });

  const form = useForm<DashboardFiltersForm>({
    defaultValues: {
      year: currentYear,
      branch_groups: [],
      region: "",
    },
  });

  const groups = useQuery(
    ["report-dashboard-groups"],
    () => {
      return group.getAllBranchGroup({ keyword: "" });
    },
    {
      ...queryDefault,
    }
  );

  const reportDashboard = useQuery(
    ["report-dashboard", filters],
    ({ queryKey }) => {
      return report.getReportDashboard(normalizeParams(queryKey[1] as any));
    },
    {
      ...queryDefault,
      onError: (error: any) => {
        notification.onOpen({
          message: getErrorMessage(error, "Failed to get report dashboard data"),
          type: "error",
          position: "top",
        });
      },
    }
  );

  const downloadDashboard = useMutation(
    "download-report-dashboard",
    report.downloadReportDashboard,
    {
      onSuccess: () => {
        setDownloadError("");
      },
      onError: (error: any) => {
        setDownloadError(
          getErrorMessage(error, "Failed to download report dashboard file")
        );
      },
      ...queryDefault,
    }
  );

  const dashboardData = get(reportDashboard, "data.data", {});
  const branchGroups: BranchGroupItem[] = get(dashboardData, "branch_groups", []);
  const groupOptions = get(groups, "data.data", []);
  const regionOptions = useMemo(() => {
    const regions: string[] = Array.from(
      new Set(
        groupOptions
          .map((item: any) => String(item.region || "").trim())
          .filter(Boolean)
      )
    ) as string[];

    return regions.sort((left, right) => left.localeCompare(right));
  }, [groupOptions]);

  const summaryMonthly = useMemo<MonthlyIncome[]>(() => {
    const monthly = get(dashboardData, "total_monthly_income", []);
    return MONTHS.map((monthItem) => {
      const matched = monthly.find(
        (item: MonthlyIncome) => Number(item.month) === Number(monthItem.month)
      );
      return {
        month: monthItem.month,
        month_name: monthItem.month_name,
        total_income: Number(matched?.total_income || 0),
        total_kertas: Number(matched?.total_kertas || 0),
      };
    });
  }, [dashboardData]);

  const branchGroupInsights = useMemo(() => {
    return branchGroups.map((branchGroup) => {
      const availableMonths = MONTHS.map((monthItem) => ({
        month_name: monthItem.month_name,
        income: findMonthIncome(branchGroup.monthly_income, monthItem.month),
      })).filter((item) => item.income > 0);

      if (availableMonths.length === 0) {
        return {
          branch_group_id: branchGroup.branch_group_id,
          branch_group_name: branchGroup.branch_group_name,
          highestMonthName: "-",
          highestIncome: 0,
          lowestMonthName: "-",
          lowestIncome: 0,
        };
      }

      const sorted = [...availableMonths].sort((a, b) => b.income - a.income);
      const highest = sorted[0];
      const lowest = sorted[sorted.length - 1];

      return {
        branch_group_id: branchGroup.branch_group_id,
        branch_group_name: branchGroup.branch_group_name,
        highestMonthName: highest.month_name,
        highestIncome: highest.income,
        lowestMonthName: lowest.month_name,
        lowestIncome: lowest.income,
      };
    });
  }, [branchGroups]);

  const branchGroupRankingByMonth = useMemo(() => {
    return MONTHS.map((monthItem) => {
      const rankings = branchGroups
        .map((branchGroup) => ({
          branch_group_id: branchGroup.branch_group_id,
          branch_group_name: branchGroup.branch_group_name,
          income: findMonthIncome(branchGroup.monthly_income, monthItem.month),
        }))
        .filter((item) => item.income > 0)
        .sort((a, b) => b.income - a.income);

      return {
        month: monthItem.month,
        month_name: monthItem.month_name,
        rankings,
      };
    });
  }, [branchGroups]);

  const branchRankingByMonth = useMemo(() => {
    return MONTHS.map((monthItem) => {
      const rows: {
        branch_id: number;
        branch_name: string;
        branch_group_name: string;
        income: number;
      }[] = [];

      branchGroups.forEach((branchGroup) => {
        (branchGroup.branches || []).forEach((branch) => {
          rows.push({
            branch_id: branch.branch_id,
            branch_name: branch.branch_name,
            branch_group_name: branchGroup.branch_group_name,
            income: findMonthIncome(branch.monthly_income, monthItem.month),
          });
        });
      });

      return {
        month: monthItem.month,
        month_name: monthItem.month_name,
        rankings: rows.filter((item) => item.income > 0).sort((a, b) => b.income - a.income),
      };
    });
  }, [branchGroups]);

  const onSubmitFilter = (values: DashboardFiltersForm) => {
    const selectedIds = (values.branch_groups || []).map((item: any) => Number(item.id));

    setDownloadError("");
    setSalesDownloadError("");
    setFilters({
      year: Number(values.year),
      branchGroupIds: selectedIds,
      region: String(values.region || "").trim(),
    });
  };

  const onResetFilter = () => {
    form.reset({
      year: currentYear,
      branch_groups: [],
      region: "",
    });

    setExpandedRows({});
    setDownloadError("");
    setSalesDownloadError("");
    setFilters({
      year: currentYear,
      branchGroupIds: [],
      region: "",
    });
  };

  const toggleRow = (branchGroupId: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [branchGroupId]: !prev[branchGroupId],
    }));
  };

  const onDownload = () => {
    downloadDashboard.mutate(normalizeParams(filters));
  };

  const onRetryDownload = () => {
    onDownload();
  };

  const onDownloadSalesExcel = () => {
    try {
      setSalesDownloadError("");
      setIsSalesDownloading(true);

      const workbook = XLSX.utils.book_new();

      const insightsSheetData = branchGroupInsights.map((item) => ({
        "Branch Group": item.branch_group_name,
        "Highest Income Month": item.highestMonthName,
        "Highest Income": `Rp ${formatCurrency(item.highestIncome)}`,
        "Lowest Income Month": item.lowestMonthName,
        "Lowest Income": `Rp ${formatCurrency(item.lowestIncome)}`,
      }));

      const branchGroupMaxRank = Math.max(
        ...branchGroupRankingByMonth.map((item) => item.rankings.length),
        0
      );

      const branchGroupRankingSheetData = Array.from(
        { length: branchGroupMaxRank },
        (_, rankIndex) => {
          const row: Record<string, string | number> = {
            Rank: rankIndex + 1,
          };

          branchGroupRankingByMonth.forEach((monthItem) => {
            const rankItem = monthItem.rankings[rankIndex];
            row[monthItem.month_name] = rankItem
              ? `${rankItem.branch_group_name} (Rp ${formatCurrency(rankItem.income)})`
              : "-";
          });

          return row;
        }
      );

      const branchMaxRank = Math.max(
        ...branchRankingByMonth.map((item) => item.rankings.length),
        0
      );

      const branchRankingSheetData = Array.from({ length: branchMaxRank }, (_, rankIndex) => {
        const row: Record<string, string | number> = {
          Rank: rankIndex + 1,
        };

        branchRankingByMonth.forEach((monthItem) => {
          const rankItem = monthItem.rankings[rankIndex];
          row[monthItem.month_name] = rankItem
            ? `${rankItem.branch_name} - ${rankItem.branch_group_name} (Rp ${formatCurrency(
                rankItem.income
              )})`
            : "-";
        });

        return row;
      });

      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(insightsSheetData),
        "Branch Group Details"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(branchGroupRankingSheetData),
        "Branch Group Ranking"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(branchRankingSheetData),
        "Branch Ranking"
      );

      XLSX.writeFile(workbook, `report_dashboard_sales_${filters.year}.xlsx`);
    } catch (error: any) {
      setSalesDownloadError(
        getErrorMessage(error, "Failed to generate sales excel file")
      );
    } finally {
      setIsSalesDownloading(false);
    }
  };

  return (
    <MainLayout>
      <Helmet>
        <title>REPORT DASHBOARD</title>
      </Helmet>

      <Breadcrumb
        label="Report Dashboard"
        breadcrumbs={[
          { label: "Report", href: "/app/report" },
          { label: "Report Dashboard", href: "/app/report/dashboard" },
        ]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2, 3]}>
        <Box sx={{ mt: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "end" }}
          >
            <FormControl sx={{ width: { xs: "100%", md: 220 } }}>
              <FormLabel required>Year</FormLabel>
              <Controller
                name="year"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    placeholder="YYYY"
                    inputProps={{ min: 1000, max: 9999 }}
                  />
                )}
              />
            </FormControl>

            <FormControl sx={{ width: { xs: "100%", md: 420 } }}>
              <FormLabel>Branch Group</FormLabel>
              <Controller
                name="branch_groups"
                control={form.control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={groupOptions}
                    value={field.value || []}
                    getOptionLabel={(option: any) => String(option.name || "")}
                    isOptionEqualToValue={(option: any, value: any) =>
                      Number(option.id) === Number(value.id)
                    }
                    onChange={(_, newValue) => {
                      field.onChange(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select branch group(s)"
                      />
                    )}
                  />
                )}
              />
            </FormControl>

            <FormControl sx={{ width: { xs: "100%", md: 260 } }}>
              <FormLabel>Region</FormLabel>
              <Controller
                name="region"
                control={form.control}
                render={({ field }) => (
                  <Autocomplete
                    options={regionOptions}
                    value={field.value || null}
                    onChange={(_, newValue) => {
                      field.onChange(newValue || "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select region"
                      />
                    )}
                  />
                )}
              />
            </FormControl>

            <Stack direction="row" spacing={1}>
              <LoadingButton
                variant="contained"
                onClick={form.handleSubmit(onSubmitFilter)}
                loading={reportDashboard.isFetching}
              >
                Apply
              </LoadingButton>

              <Button variant="outlined" onClick={onResetFilter}>
                Reset
              </Button>
            </Stack>
          </Stack>
        </Box>

        {downloadError ? (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            action={
              <Button color="inherit" size="small" onClick={onRetryDownload}>
                Retry
              </Button>
            }
          >
            {downloadError}
          </Alert>
        ) : null}

        <Box sx={{ mt: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, value: DashboardTab) => setActiveTab(value)}
          >
            <Tab label="Summary" value="summary" />
            <Tab label="Sales" value="sales" />
          </Tabs>
        </Box>

        {activeTab === "summary" ? (
          <Box sx={{ mt: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Typography variant="h6" fontWeight={700}>
                Summary Section
              </Typography>

              <LoadingButton
                variant="contained"
                color="success"
                onClick={onDownload}
                loading={downloadDashboard.isLoading}
              >
                Download Summary Excel
              </LoadingButton>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
                {summaryMonthly.map((item) => (
                  <Box
                    key={item.month}
                    sx={{
                      minWidth: 150,
                      border: "1px solid #e3e3e3",
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {item.month_name}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      Rp {formatCurrency(item.total_income)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatNumber(item.total_kertas)} kertas
                    </Typography>
                  </Box>
                ))}
              </Stack>

              <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>
                Annual Total: Rp {formatCurrency(Number(get(dashboardData, "total_annual_income", 0)))}
              </Typography>
            </Paper>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Summary Table
              </Typography>

              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 1300 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 56 }} />
                      <TableCell sx={{ minWidth: 220, fontWeight: 700 }}>Name</TableCell>
                      {MONTHS.map((item) => (
                        <TableCell key={item.month} align="right" sx={{ fontWeight: 700 }}>
                          {item.month_name}
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ fontWeight: 700, minWidth: 130 }}>
                        Annual Total
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {branchGroups.map((branchGroup) => {
                      const expanded = Boolean(expandedRows[branchGroup.branch_group_id]);

                      return (
                        <>
                          <TableRow
                            key={`group-${branchGroup.branch_group_id}`}
                            sx={{
                              backgroundColor: "#f8fafc",
                            }}
                          >
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => toggleRow(branchGroup.branch_group_id)}
                              >
                                {expanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              {branchGroup.branch_group_name}
                            </TableCell>
                            {MONTHS.map((monthItem) => (
                              <TableCell key={monthItem.month} align="right" sx={{ fontWeight: 700 }}>
                                <Typography variant="body2" fontWeight={700}>
                                  Rp {formatCurrency(findMonthIncome(branchGroup.monthly_income, monthItem.month))}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Kertas: {formatNumber(findMonthKertas(branchGroup.monthly_income, monthItem.month))}
                                </Typography>
                              </TableCell>
                            ))}
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              <Typography variant="body2" fontWeight={700}>
                                Rp {formatCurrency(branchGroup.total_annual_income)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Kertas: {formatNumber(branchGroup.total_annual_kertas || 0)}
                              </Typography>
                            </TableCell>
                          </TableRow>

                          {expanded &&
                            (branchGroup.branches || []).map((branchItem) => (
                              <TableRow key={`branch-${branchItem.branch_id}`}>
                                <TableCell />
                                <TableCell sx={{ pl: 5 }}>{branchItem.branch_name}</TableCell>
                                {MONTHS.map((monthItem) => (
                                  <TableCell key={monthItem.month} align="right">
                                    <Typography variant="body2" fontWeight={700}>
                                      Rp {formatCurrency(findMonthIncome(branchItem.monthly_income, monthItem.month))}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Kertas: {formatNumber(findMonthKertas(branchItem.monthly_income, monthItem.month))}
                                    </Typography>
                                  </TableCell>
                                ))}
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={700}>
                                    Rp {formatCurrency(branchItem.total_annual_income)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Kertas: {formatNumber(branchItem.total_annual_kertas || 0)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                        </>
                      );
                    })}

                    {!reportDashboard.isLoading && branchGroups.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={15} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No data found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        ) : null}

        {activeTab === "sales" ? (
          <Box sx={{ mt: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Typography variant="h6" fontWeight={700}>
                Sales Section
              </Typography>

              <LoadingButton
                variant="contained"
                color="success"
                onClick={onDownloadSalesExcel}
                loading={isSalesDownloading}
              >
                Download Sales Excel
              </LoadingButton>
            </Stack>

            {salesDownloadError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {salesDownloadError}
              </Alert>
            ) : null}

            <Box sx={{ mb: 2 }}>
              <Tabs
                value={activeSalesTab}
                onChange={(_, value: SalesTab) => setActiveSalesTab(value)}
              >
                <Tab label="Branch Group Details" value="branch_group_details" />
                <Tab label="Branch Group Ranking" value="branch_group_ranking" />
                <Tab label="Branch Ranking" value="branch_ranking" />
              </Tabs>
            </Box>

            {activeSalesTab === "branch_group_details" ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Branch Group Details
                </Typography>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Branch Group</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Highest Income Month</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Lowest Income Month</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {branchGroupInsights.map((item) => (
                        <TableRow key={item.branch_group_id}>
                          <TableCell>{item.branch_group_name}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {item.highestMonthName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Rp {formatCurrency(item.highestIncome)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {item.lowestMonthName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Rp {formatCurrency(item.lowestIncome)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : null}

            {activeSalesTab === "branch_group_ranking" ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Branch Group Ranking
                </Typography>

                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 2200 }}>
                    <TableHead>
                      <TableRow>
                        {branchGroupRankingByMonth.map((monthItem) => (
                          <TableCell
                            key={`bg-ranking-head-${monthItem.month}`}
                            sx={{ fontWeight: 700, minWidth: 160 }}
                          >
                            {monthItem.month_name}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        {branchGroupRankingByMonth.map((monthItem) => (
                          <TableCell key={`bg-ranking-${monthItem.month}`} sx={{ verticalAlign: "top" }}>
                            {monthItem.rankings.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No available data
                              </Typography>
                            ) : (
                              <Stack spacing={0.75}>
                                {monthItem.rankings.map((item, index) => (
                                  <Box key={`${monthItem.month}-${item.branch_group_id}`}>
                                    <Typography variant="body2" fontWeight={700}>
                                      {index + 1}. {item.branch_group_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Rp {formatCurrency(item.income)}
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : null}

            {activeSalesTab === "branch_ranking" ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Branch Ranking
                </Typography>

                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 2200 }}>
                    <TableHead>
                      <TableRow>
                        {branchRankingByMonth.map((monthItem) => (
                          <TableCell
                            key={`branch-ranking-head-${monthItem.month}`}
                            sx={{ fontWeight: 700, minWidth: 160 }}
                          >
                            {monthItem.month_name}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        {branchRankingByMonth.map((monthItem) => (
                          <TableCell
                            key={`branch-ranking-${monthItem.month}`}
                            sx={{ verticalAlign: "top" }}
                          >
                            {monthItem.rankings.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No available data
                              </Typography>
                            ) : (
                              <Stack spacing={0.75}>
                                {monthItem.rankings.map((item, index) => (
                                  <Box key={`${monthItem.month}-${item.branch_id}`}>
                                    <Typography variant="body2" fontWeight={700}>
                                      {index + 1}. {item.branch_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {item.branch_group_name} - Rp {formatCurrency(item.income)}
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : null}
          </Box>
        ) : null}
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
